import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

// ============================================================================
// NIRA — Nirvikalp Intelligent ERP Assistant
// Tab 1: AI Chat (live DB queries)
// Tab 2: Team Chat (real-time messaging between all ERP users + suppliers)
//         — Click any online user to open a DM
//         — Click 📞 to start an audio call, 📹 for video (WebRTC, peer-to-peer)
// ============================================================================

const CHATBOT_URL  = 'https://nirvikalp.integrashion.com/api/chatbot.php';
const CHAT_API_URL = 'https://nirvikalp.integrashion.com/api/chat_api.php';

const MSG_POLL_MS       = 2000;
const PRESENCE_POLL_MS  = 5000;
const SIGNAL_POLL_MS    = 1500;
const HEARTBEAT_MS      = 12000;

const ICE_SERVERS = { iceServers: [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun.cloudflare.com:3478' },
]};

const ROLES = [
  { id:'admin',         label:'Admin',           emoji:'🔑', color:'#0f172a', desc:'Full access to all modules' },
  { id:'merchandising', label:'Merchandising',    emoji:'📦', color:'#8b5cf6', desc:'Styles, POs, Costing' },
  { id:'material',      label:'Store / Material', emoji:'🧵', color:'#2563eb', desc:'Fabric, Trims, Inventory' },
  { id:'production',    label:'Production',       emoji:'🏭', color:'#059669', desc:'Cut, Sew, Pack, Ship' },
  { id:'quality',       label:'Quality',          emoji:'✅', color:'#d97706', desc:'Inspections, Defects, QC' },
  { id:'sourcing',      label:'Sourcing',         emoji:'🔍', color:'#db2777', desc:'Suppliers, Quotes, BoM' },
  { id:'supplier',      label:'Supplier',         emoji:'🚢', color:'#0891b2', desc:'Your orders & deliveries only' },
];
const RC = Object.fromEntries(ROLES.map(r=>[r.id,r.color]));
const RE = Object.fromEntries(ROLES.map(r=>[r.id,r.emoji]));

const AI_QUICK = {
  admin:         ['Show all critical stock alerts','Summarise today\'s production','Which styles have QC failures?'],
  merchandising: ['List active styles and PO status','Which POs are overdue?','Order book by buyer'],
  material:      ['Show critical fabric rolls','Stock shortage by style','Received vs issued this week'],
  production:    ['Cut vs sew gap by style','Which lines are behind?','Styles ready to pack'],
  quality:       ['FAIL inspections this week','Rolls with defect pts > 20','Fabric claims open'],
  sourcing:      ['Suppliers with open POs','Pending quotes by style','BoM summary'],
  supplier:      ['My open POs and delivery dates','QC inspection status','Any rejections on my fabric?'],
};

// ── API helper ────────────────────────────────────────────────────────────────
const chatPost = async (action, params={}) => {
  try {
    const r = await fetch(CHAT_API_URL, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({action,...params}) });
    return await r.json();
  } catch(e) { return {success:false,error:e.message}; }
};

function fmtTime(ts) {
  if (!ts) return '';
  const d = new Date(ts), now = new Date(), diff = (now-d)/1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return Math.floor(diff/60)+'m ago';
  if (d.toDateString()===now.toDateString()) return d.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'});
  return d.toLocaleDateString('en-IN',{day:'2-digit',month:'short'});
}

// Minimal markdown
function MD({ text }) {
  if (!text) return null;
  return <div>{text.split('\n').map((line,i) => {
    if (!line.trim()) return <div key={i} style={{height:5}}/>;
    const bold = s => s.split(/\*\*(.+?)\*\*/g).map((p,pi)=>pi%2===1?<strong key={pi}>{p}</strong>:p);
    if (line.startsWith('###')) return <div key={i} style={{fontWeight:800,fontSize:13,marginTop:10,marginBottom:3}}>{line.replace(/^#+\s*/,'')}</div>;
    if (line.startsWith('- ')||line.startsWith('• ')) return <div key={i} style={{display:'flex',gap:6,fontSize:13,marginBottom:2}}><span style={{color:'#94a3b8',flexShrink:0}}>•</span><span>{bold(line.slice(2))}</span></div>;
    if (line.includes('⚠️')||line.includes('🚨')) return <div key={i} style={{fontSize:13,color:'#dc2626',background:'#fef2f2',padding:'3px 8px',borderRadius:6,marginBottom:2,border:'1px solid #fecaca'}}>{bold(line)}</div>;
    if (line.includes('✅')) return <div key={i} style={{fontSize:13,color:'#16a34a',marginBottom:2}}>{bold(line)}</div>;
    return <div key={i} style={{fontSize:13,color:'#334155',lineHeight:1.55,marginBottom:1}}>{bold(line)}</div>;
  })}</div>;
}

function Dots() {
  return <div style={{display:'flex',gap:4,alignItems:'center',padding:'8px 12px'}}>
    {[0,1,2].map(i=><div key={i} style={{width:7,height:7,borderRadius:'50%',background:'#94a3b8',animation:'niraBounce 1.2s ease-in-out infinite',animationDelay:i*0.2+'s'}}/>)}
  </div>;
}

function StatusDot({status,size=8}) {
  return <div style={{width:size,height:size,borderRadius:'50%',background:status==='online'?'#16a34a':'#cbd5e1',border:'1.5px solid white',flexShrink:0}}/>;
}

// ── WebRTC Call Overlay ───────────────────────────────────────────────────────
function CallOverlay({state,local,remote,onHangup,onAccept}) {
  const remRef=useRef(), locRef=useRef();
  useEffect(()=>{ if(remRef.current&&remote) remRef.current.srcObject=remote; },[remote]);
  useEffect(()=>{ if(locRef.current&&local)  locRef.current.srcObject=local;  },[local]);
  const {phase,type,isIncoming,callerName} = state;
  return (
    <div style={{position:'fixed',inset:0,zIndex:10001,background:'rgba(15,23,42,0.96)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',animation:'niraPop 0.2s ease-out'}}>
      {type==='video'&&remote && <video ref={remRef} autoPlay playsInline style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover',opacity:0.75}}/>}
      {type==='video'&&local  && <video ref={locRef} autoPlay playsInline muted style={{position:'absolute',bottom:100,right:20,width:130,height:88,borderRadius:10,objectFit:'cover',border:'2px solid white'}}/>}
      <div style={{position:'relative',textAlign:'center'}}>
        <div style={{width:110,height:110,borderRadius:'50%',background:'linear-gradient(135deg,#2563eb,#7c3aed)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:46,margin:'0 auto 20px',boxShadow:phase==='active'?'0 0 0 0 rgba(37,99,235,0.4)':'none',animation:phase==='active'?'niraPulse 2s ease-in-out infinite':'none'}}>👤</div>
        <div style={{color:'white',fontWeight:800,fontSize:20,marginBottom:6}}>{callerName}</div>
        <div style={{color:'#94a3b8',fontSize:14,marginBottom:32}}>
          {phase==='ringing'&&isIncoming&&'📞 Incoming call…'}
          {phase==='ringing'&&!isIncoming&&'📞 Calling…'}
          {phase==='connecting'&&'⏳ Connecting…'}
          {phase==='active'&&`🔴 ${type==='video'?'Video':'Audio'} call active`}
          {phase==='ended'&&'📵 Call ended'}
        </div>
        <div style={{display:'flex',gap:14,justifyContent:'center'}}>
          {phase==='ringing'&&isIncoming&&<>
            <button onClick={()=>onAccept('audio')} style={{width:62,height:62,borderRadius:'50%',background:'#16a34a',border:'none',fontSize:28,cursor:'pointer',boxShadow:'0 4px 16px rgba(22,163,74,0.4)'}}>📞</button>
            <button onClick={()=>onAccept('video')} style={{width:62,height:62,borderRadius:'50%',background:'#2563eb',border:'none',fontSize:28,cursor:'pointer'}}>📹</button>
          </>}
          <button onClick={onHangup} style={{width:62,height:62,borderRadius:'50%',background:'#dc2626',border:'none',fontSize:28,cursor:'pointer',boxShadow:'0 4px 16px rgba(220,38,38,0.4)'}}>📵</button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Chatbot({ db, currentModule='' }) {
  const [open,      setOpen]      = useState(false);
  const [tab,       setTab]       = useState('ai');    // 'ai' | 'team'
  const [role,      setRole]      = useState(null);
  const [userName,  setUserName]  = useState('');
  const [suppName,  setSuppName]  = useState('');
  const [roleScreen,setRoleScreen]= useState(false);
  const [fabUnread, setFabUnread] = useState(0);

  // AI
  const [aiMsgs,   setAiMsgs]   = useState([]);
  const [aiInput,  setAiInput]  = useState('');
  const [aiLoading,setAiLoading]= useState(false);
  const [aiErr,    setAiErr]    = useState('');

  // Team chat
  const [rooms,      setRooms]     = useState([]);
  const [room,       setRoom]      = useState('general');
  const [msgMap,     setMsgMap]    = useState({});
  const [lastIdMap,  setLastIdMap] = useState({});
  const [chatIn,     setChatIn]    = useState('');
  const [presence,   setPresence]  = useState([]);
  const [unreadMap,  setUnreadMap] = useState({});
  const [sending,    setSending]   = useState(false);

  // WebRTC
  const [callState, setCallState] = useState(null);
  const [localSt,   setLocalSt]   = useState(null);
  const [remoteSt,  setRemoteSt]  = useState(null);
  const pcRef     = useRef(null);
  const pendCands = useRef([]);

  const userId = useMemo(()=>{
    if (!role||!userName) return null;
    const hash = userName.split('').reduce((a,c)=>a+c.charCodeAt(0),0);
    return `${role}_${userName.toLowerCase().replace(/\s+/g,'_')}_${Math.abs(hash)}`;
  },[role,userName]);

  const ro = ROLES.find(r=>r.id===role);
  const rc = ro?.color||'#2563eb';

  const aiEnd   = useRef(); const chatEnd = useRef();
  const chatRef = useRef(); const aiRef   = useRef();

  useEffect(()=>{ if(open&&tab==='ai')   aiEnd.current?.scrollIntoView({behavior:'smooth'}); },[aiMsgs,aiLoading]);
  useEffect(()=>{ if(open&&tab==='team') chatEnd.current?.scrollIntoView({behavior:'smooth'}); },[msgMap,room]);
  useEffect(()=>{ if(open) setFabUnread(0); },[open]);

  // ── Register & heartbeat ─────────────────────────────────────────────────
  useEffect(()=>{
    if (!userId||!role) return;
    const base={user_id:userId,user_name:userName,role,supplier_name:suppName};
    chatPost('register',base);
    fetchRooms(); fetchPresence();
    const hb = setInterval(()=>chatPost('heartbeat',{user_id:userId,user_name:userName,role}),HEARTBEAT_MS);
    return ()=>clearInterval(hb);
  },[userId]);

  // ── Message poll ─────────────────────────────────────────────────────────
  useEffect(()=>{
    if (!userId||!open||tab!=='team') return;
    const poll = async()=>{
      const sinceId = lastIdMap[room]||0;
      const res = await chatPost('get_messages',{user_id:userId,role,room,since_id:sinceId,limit:sinceId?30:50});
      if (res.success&&res.messages?.length>0) {
        setMsgMap(prev=>{
          const ex=prev[room]||[], exIds=new Set(ex.map(m=>String(m.id)));
          const add=res.messages.filter(m=>!exIds.has(String(m.id)));
          return {...prev,[room]:[...ex,...add]};
        });
        const maxId=Math.max(...res.messages.map(m=>parseInt(m.id)));
        setLastIdMap(prev=>({...prev,[room]:Math.max(prev[room]||0,maxId)}));
      }
    };
    poll();
    const t=setInterval(poll,MSG_POLL_MS);
    return ()=>clearInterval(t);
  },[userId,open,tab,room]);

  // ── Presence poll ────────────────────────────────────────────────────────
  useEffect(()=>{
    if (!userId||!open||tab!=='team') return;
    const poll=async()=>{ const r=await chatPost('get_presence',{user_id:userId,role}); if(r.success) setPresence(r.users||[]); };
    poll(); const t=setInterval(poll,PRESENCE_POLL_MS); return ()=>clearInterval(t);
  },[userId,open,tab]);

  // ── Unread background poll ───────────────────────────────────────────────
  useEffect(()=>{
    if (!userId) return;
    const poll=async()=>{
      const r=await chatPost('get_rooms',{user_id:userId,role});
      if (r.success){ setUnreadMap(r.unread||{}); if(!open) setFabUnread(Object.values(r.unread||{}).reduce((s,v)=>s+v,0)); }
    };
    const t=setInterval(poll,8000); return ()=>clearInterval(t);
  },[userId,open]);

  // ── Signal poll (WebRTC) ─────────────────────────────────────────────────
  useEffect(()=>{
    if (!userId) return;
    const poll=async()=>{
      const r=await chatPost('get_signals',{user_id:userId,role,user_name:userName});
      if (!r.success||!r.signals?.length) return;
      for (const sig of r.signals) await handleSignal(sig,r.caller_names||{});
    };
    const t=setInterval(poll,SIGNAL_POLL_MS); return ()=>clearInterval(t);
  },[userId,callState]);

  // ── WebRTC ───────────────────────────────────────────────────────────────
  const mkPC = useCallback(()=>{
    const pc=new RTCPeerConnection(ICE_SERVERS);
    pc.onicecandidate=e=>{ if(e.candidate&&pcRef.current?._peer) chatPost('send_signal',{user_id:userId,user_name:userName,role,to_user:pcRef.current._peer,signal_type:'candidate',signal_data:e.candidate}); };
    pc.ontrack=e=>{ setRemoteSt(e.streams[0]); setCallState(p=>p?{...p,phase:'active'}:p); };
    pc.onconnectionstatechange=()=>{ if(['disconnected','failed','closed'].includes(pc.connectionState)) cleanCall(); };
    return pc;
  },[userId,userName,role]);

  const getMedia=async type=>{
    try { const s=await navigator.mediaDevices.getUserMedia({audio:true,video:type==='video'}); setLocalSt(s); return s; }
    catch(e){ alert('Microphone/camera permission denied. Please allow in browser.'); return null; }
  };

  const startCall=async(targetId,targetName,type='audio')=>{
    const stream=await getMedia(type); if(!stream) return;
    const pc=mkPC(); pcRef.current=pc; pcRef.current._peer=targetId;
    stream.getTracks().forEach(t=>pc.addTrack(t,stream));
    const offer=await pc.createOffer(); await pc.setLocalDescription(offer);
    await chatPost('send_signal',{user_id:userId,user_name:userName,role,to_user:targetId,signal_type:'offer',signal_data:{sdp:offer,call_type:type,caller_name:userName}});
    setCallState({phase:'ringing',type,isIncoming:false,peerId:targetId,callerName:targetName});
    openDM(targetId,targetName);
  };

  const handleSignal=async(sig,cnames)=>{
    const {from_user,signal_type,signal_data}=sig;
    if (signal_type==='offer'){
      const cn=cnames[from_user]||signal_data?.caller_name||from_user;
      const ct=signal_data?.call_type||'audio';
      if (!pcRef.current) pcRef.current=mkPC();
      pcRef.current._peer=from_user; pcRef.current._offer=signal_data?.sdp; pcRef.current._ct=ct;
      setCallState({phase:'ringing',type:ct,isIncoming:true,peerId:from_user,callerName:cn});
      if (!open) setFabUnread(u=>u+1);
    } else if (signal_type==='answer'&&pcRef.current){
      await pcRef.current.setRemoteDescription(new RTCSessionDescription(signal_data?.sdp||signal_data));
      pendCands.current.forEach(c=>pcRef.current.addIceCandidate(new RTCIceCandidate(c)));
      pendCands.current=[];
    } else if (signal_type==='candidate'&&pcRef.current){
      if (pcRef.current.remoteDescription) await pcRef.current.addIceCandidate(new RTCIceCandidate(signal_data));
      else pendCands.current.push(signal_data);
    } else if (signal_type==='hangup'){ cleanCall(); }
  };

  const acceptCall=async(type)=>{
    const stream=await getMedia(type||pcRef.current?._ct||'audio'); if(!stream) return;
    const pc=pcRef.current||mkPC();
    if (!pcRef.current){ pcRef.current=pc; pcRef.current._peer=callState?.peerId; }
    stream.getTracks().forEach(t=>pc.addTrack(t,stream));
    await pc.setRemoteDescription(new RTCSessionDescription(pc._offer));
    const ans=await pc.createAnswer(); await pc.setLocalDescription(ans);
    await chatPost('send_signal',{user_id:userId,user_name:userName,role,to_user:callState.peerId,signal_type:'answer',signal_data:{sdp:ans}});
    pendCands.current.forEach(c=>pc.addIceCandidate(new RTCIceCandidate(c))); pendCands.current=[];
    setCallState(p=>({...p,phase:'active',type}));
  };

  const hangup=useCallback(()=>{
    if (callState?.peerId) chatPost('send_signal',{user_id:userId,user_name:userName,role,to_user:callState.peerId,signal_type:'hangup',signal_data:{}});
    cleanCall();
  },[callState,userId]);

  const cleanCall=()=>{
    localSt?.getTracks().forEach(t=>t.stop());
    setLocalSt(null); setRemoteSt(null);
    if(pcRef.current){pcRef.current.close();pcRef.current=null;}
    setCallState(null);
  };

  // ── Room helpers ─────────────────────────────────────────────────────────
  const fetchRooms=async()=>{
    const r=await chatPost('get_rooms',{user_id:userId,role}); if(r.success){setRooms(r.rooms||[]);setUnreadMap(r.unread||{});}
  };
  const fetchPresence=async()=>{
    const r=await chatPost('get_presence',{user_id:userId,role}); if(r.success) setPresence(r.users||[]);
  };
  const openDM=async(targetId,targetName)=>{
    const r=await chatPost('start_dm',{user_id:userId,user_name:userName,role,target_id:targetId});
    if (r.success){
      setRoom(r.room); setTab('team');
      setRooms(prev=>prev.find(x=>x.id===r.room)?prev:[...prev,{id:r.room,name:targetName,icon:'👤',type:'dm',other_id:targetId}]);
    }
  };

  const sendMsg=async()=>{
    const text=chatIn.trim(); if(!text||!userId||sending) return;
    setSending(true); setChatIn('');
    const tmp={id:'tmp_'+Date.now(),sender_id:userId,sender_name:userName,sender_role:role,message:text,msg_type:'text',created_at:new Date().toISOString()};
    setMsgMap(p=>({...p,[room]:[...(p[room]||[]),tmp]}));
    await chatPost('send_message',{user_id:userId,user_name:userName,role,room,message:text});
    setSending(false);
  };

  // ── AI chat ──────────────────────────────────────────────────────────────
  const sendAI=async(text)=>{
    const t=(text||aiInput).trim(); if(!t||aiLoading) return;
    setAiInput(''); setAiErr('');
    const newMsgs=[...aiMsgs,{role:'user',content:t,ts:Date.now()}];
    setAiMsgs(newMsgs); setAiLoading(true);
    try {
      const res=await fetch(CHATBOT_URL,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({role,supplier_name:suppName,messages:newMsgs.map(m=>({role:m.role,content:m.content})),context_summary:`Module:${currentModule}`})});
      const d=await res.json(); if(!d.success) throw new Error(d.error);
      setAiMsgs(p=>[...p,{role:'assistant',content:d.reply,ts:Date.now()}]);
    } catch(e){ setAiErr(e.message); setAiMsgs(p=>[...p,{role:'assistant',content:'⚠️ Connection issue. Please try again.',ts:Date.now()}]); }
    setAiLoading(false);
  };

  useEffect(()=>{
    if (!role) return;
    const w={supplier:`Hi${suppName?', **'+suppName+'**':''}! 👋 I can show your POs, QC, delivery status.`,material:'Hello! 🧵 Live inventory visibility — stock, shortages, received/issued.',production:'Hi! 🏭 Cut/sew/pack/ship status for any style.',quality:'Hello! ✅ Inspection results, defects, approvals at your fingertips.',merchandising:'Hi! 📦 PO status, style progress, costings — what are you looking for?',sourcing:'Hi! 🔍 Suppliers, quotes, BoM — ask me anything.',admin:'Welcome! 🔑 Full ERP access. What would you like to know?'};
    setAiMsgs([{role:'assistant',content:w[role]||'Hi! How can I help?',ts:Date.now()}]);
  },[role]);

  const onlineOthers = presence.filter(u=>u.user_id!==userId&&u.status==='online');
  const totalUnread  = Object.values(unreadMap).reduce((s,v)=>s+v,0);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @keyframes niraBounce{0%,80%,100%{transform:translateY(0)}40%{transform:translateY(-6px)}}
        @keyframes niraSlideUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes niraPop{from{opacity:0;transform:scale(0.88)}to{opacity:1;transform:scale(1)}}
        @keyframes niraPulse{0%,100%{box-shadow:0 0 0 0 rgba(37,99,235,0.45)}60%{box-shadow:0 0 0 12px rgba(37,99,235,0)}}
        .n-fab:hover{transform:scale(1.1)!important}
        .n-rm:hover{background:#f1f5f9!important}
        .n-rm.act{background:#eff6ff!important;border-right:3px solid #2563eb!important;color:#1d4ed8!important;font-weight:700!important}
        .n-usr:hover{background:#f8fafc!important;cursor:pointer}
        .n-qk:hover{background:#eff6ff!important;border-color:#2563eb!important;color:#1d4ed8!important}
        .n-sc::-webkit-scrollbar{width:3px;height:3px}
        .n-sc::-webkit-scrollbar-thumb{background:#e2e8f0;border-radius:4px}
        .n-role:hover{background:var(--rc)!important;border-color:var(--rc)!important}
      `}</style>

      {callState && <CallOverlay state={callState} local={localSt} remote={remoteSt} onHangup={hangup} onAccept={acceptCall}/>}

      {/* FAB */}
      {!open&&(
        <button className="n-fab" onClick={()=>setOpen(true)} style={{position:'fixed',bottom:28,right:28,zIndex:9999,width:62,height:62,borderRadius:'50%',border:'none',background:'linear-gradient(135deg,#1e3a5f,#7c3aed)',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:28,transition:'transform 0.2s',boxShadow:'0 4px 20px rgba(37,99,235,0.35)',animation:'niraPulse 3s ease-in-out infinite'}}>
          🤖
          {(fabUnread+totalUnread)>0&&<span style={{position:'absolute',top:-3,right:-3,background:'#dc2626',color:'white',borderRadius:'50%',width:20,height:20,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:800,border:'2px solid white'}}>{Math.min(99,fabUnread+totalUnread)}</span>}
        </button>
      )}

      {/* Panel */}
      {open&&(
        <div style={{position:'fixed',bottom:14,right:14,zIndex:9999,width:tab==='team'?700:420,maxWidth:'calc(100vw - 20px)',height:670,maxHeight:'calc(100vh - 28px)',background:'white',borderRadius:18,boxShadow:'0 28px 72px rgba(0,0,0,0.22),0 4px 20px rgba(0,0,0,0.1)',display:'flex',flexDirection:'column',overflow:'hidden',animation:'niraSlideUp 0.22s ease-out',transition:'width 0.25s ease'}}>

          {/* Header */}
          <div style={{background:'linear-gradient(135deg,#0f172a 0%,#1e3a5f 100%)',padding:'13px 16px',display:'flex',alignItems:'center',gap:10,flexShrink:0}}>
            <div style={{width:38,height:38,borderRadius:'50%',background:'rgba(255,255,255,0.12)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:20}}>🤖</div>
            <div style={{flex:1}}>
              <div style={{color:'white',fontWeight:800,fontSize:15,display:'flex',alignItems:'center',gap:8}}>
                NIRA
                <span style={{background:'#16a34a',color:'white',fontSize:9,fontWeight:700,padding:'2px 6px',borderRadius:20}}>LIVE</span>
                {tab==='team'&&onlineOthers.length>0&&<span style={{background:'rgba(255,255,255,0.13)',color:'#94a3b8',fontSize:11,padding:'2px 8px',borderRadius:20}}>{onlineOthers.length} online</span>}
              </div>
              <div style={{color:'#64748b',fontSize:11,marginTop:1}}>{role?`${ro?.emoji} ${ro?.label}${suppName?' · '+suppName:''}`:'Select your role to begin'}</div>
            </div>
            <div style={{display:'flex',gap:5}}>
              {role&&<button onClick={()=>setRoleScreen(true)} title="Change role" style={{background:'rgba(255,255,255,0.1)',border:'none',color:'white',borderRadius:7,padding:'5px 8px',cursor:'pointer',fontSize:14}}>👤</button>}
              <button onClick={()=>setOpen(false)} style={{background:'rgba(255,255,255,0.1)',border:'none',color:'white',borderRadius:7,padding:'5px 10px',cursor:'pointer',fontSize:19,lineHeight:1}}>×</button>
            </div>
          </div>

          {/* Tab bar */}
          {role&&!roleScreen&&(
            <div style={{display:'flex',borderBottom:'1px solid #f1f5f9',background:'#fafafa',flexShrink:0}}>
              {[{id:'ai',label:'🤖 NIRA AI'},{id:'team',label:`💬 Team${totalUnread>0?' ('+totalUnread+')':''}`}].map(t=>(
                <button key={t.id} onClick={()=>setTab(t.id)} style={{flex:1,padding:'11px',border:'none',borderBottom:tab===t.id?'2px solid #2563eb':'2px solid transparent',background:'transparent',color:tab===t.id?'#1d4ed8':'#64748b',fontWeight:700,fontSize:13,cursor:'pointer'}}>
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {/* Role selector */}
          {(!role||roleScreen)&&(
            <div className="n-sc" style={{flex:1,overflowY:'auto',padding:20,animation:'niraPop 0.2s ease-out'}}>
              <div style={{textAlign:'center',marginBottom:18}}>
                <div style={{fontSize:38,marginBottom:8}}>🤖</div>
                <div style={{fontWeight:800,fontSize:16,color:'#1e293b',marginBottom:4}}>Welcome to NIRA</div>
                <div style={{fontSize:13,color:'#64748b'}}>AI Assistant · Team Chat · Audio & Video Calling</div>
              </div>
              <div style={{marginBottom:14}}>
                <label style={{fontSize:10,fontWeight:700,color:'#64748b',display:'block',marginBottom:4,textTransform:'uppercase'}}>Your Display Name *</label>
                <input value={userName} onChange={e=>setUserName(e.target.value)} placeholder="e.g. Rahul Sharma" style={{width:'100%',padding:'9px 12px',border:'1px solid #e2e8f0',borderRadius:8,fontSize:13,outline:'none',boxSizing:'border-box'}}/>
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:7}}>
                {ROLES.map(r=>(
                  <button key={r.id} onClick={()=>{
                    if(!userName.trim()){alert('Please enter your name first');return;}
                    if(r.id==='supplier'){const sn=prompt('Enter your company/supplier name:');if(!sn?.trim())return;setSuppName(sn.trim());}
                    setRole(r.id);setRoleScreen(false);setAiMsgs([]);setMsgMap({});
                  }} style={{display:'flex',alignItems:'center',gap:12,padding:'11px 14px',border:'1px solid #e2e8f0',borderRadius:10,background:'white',cursor:'pointer',textAlign:'left',transition:'all 0.15s'}}
                    onMouseEnter={e=>{e.currentTarget.style.background=r.color+'10';e.currentTarget.style.borderColor=r.color;}}
                    onMouseLeave={e=>{e.currentTarget.style.background='white';e.currentTarget.style.borderColor='#e2e8f0';}}>
                    <div style={{width:38,height:38,borderRadius:9,background:r.color+'18',display:'flex',alignItems:'center',justifyContent:'center',fontSize:19,flexShrink:0}}>{r.emoji}</div>
                    <div style={{flex:1}}><div style={{fontWeight:700,fontSize:13,color:'#1e293b'}}>{r.label}</div><div style={{fontSize:11,color:'#64748b',marginTop:1}}>{r.desc}</div></div>
                    <div style={{color:'#cbd5e1',fontSize:18}}>›</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── AI Tab ──────────────────────────────────────────────────── */}
          {role&&!roleScreen&&tab==='ai'&&(
            <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
              <div className="n-sc" style={{flex:1,overflowY:'auto',padding:'14px 14px 6px'}}>
                {aiMsgs.map((m,i)=>{
                  const isU=m.role==='user';
                  return (
                    <div key={i} style={{display:'flex',flexDirection:isU?'row-reverse':'row',gap:8,marginBottom:10,alignItems:'flex-end'}}>
                      {!isU&&<div style={{width:30,height:30,borderRadius:'50%',background:'linear-gradient(135deg,#2563eb,#7c3aed)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,flexShrink:0}}>🤖</div>}
                      <div style={{maxWidth:'80%'}}>
                        <div style={{padding:isU?'9px 13px':'11px 14px',borderRadius:isU?'16px 16px 3px 16px':'3px 16px 16px 16px',background:isU?rc:'white',color:isU?'white':'#334155',boxShadow:isU?'none':'0 1px 4px rgba(0,0,0,0.07)',border:isU?'none':'1px solid #e2e8f0',fontSize:13,lineHeight:1.5}}>
                          {isU?m.content:<MD text={m.content}/>}
                        </div>
                        <div style={{fontSize:10,color:'#94a3b8',marginTop:2,textAlign:isU?'right':'left',padding:'0 4px'}}>{fmtTime(m.ts)}</div>
                      </div>
                    </div>
                  );
                })}
                {aiLoading&&(
                  <div style={{display:'flex',gap:8,alignItems:'flex-end',marginBottom:10}}>
                    <div style={{width:30,height:30,borderRadius:'50%',background:'linear-gradient(135deg,#2563eb,#7c3aed)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14}}>🤖</div>
                    <div style={{background:'white',border:'1px solid #e2e8f0',borderRadius:'3px 16px 16px 16px',boxShadow:'0 1px 4px rgba(0,0,0,0.07)'}}><Dots/></div>
                  </div>
                )}
                <div ref={aiEnd}/>
              </div>
              {aiMsgs.length<=1&&(AI_QUICK[role]||[]).length>0&&(
                <div style={{padding:'0 12px 8px',flexShrink:0}}>
                  <div style={{fontSize:10,color:'#94a3b8',fontWeight:700,textTransform:'uppercase',marginBottom:5}}>Quick questions</div>
                  <div style={{display:'flex',flexWrap:'wrap',gap:5}}>
                    {(AI_QUICK[role]||[]).map((q,i)=>(
                      <button key={i} className="n-qk" onClick={()=>sendAI(q)} style={{padding:'4px 10px',borderRadius:20,fontSize:11,fontWeight:600,border:'1px solid #e2e8f0',background:'#f8fafc',color:'#475569',cursor:'pointer',whiteSpace:'nowrap'}}>{q}</button>
                    ))}
                  </div>
                </div>
              )}
              {aiErr&&<div style={{margin:'0 12px 6px',padding:'7px 12px',background:'#fef2f2',border:'1px solid #fecaca',borderRadius:7,fontSize:11,color:'#dc2626',flexShrink:0}}>⚠️ {aiErr}</div>}
              <div style={{padding:'8px 12px 12px',borderTop:'1px solid #f1f5f9',background:'#fafafa',flexShrink:0}}>
                <div style={{display:'flex',gap:7,alignItems:'flex-end'}}>
                  <textarea ref={aiRef} value={aiInput} onChange={e=>setAiInput(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();sendAI();}}} placeholder="Ask anything about your ERP data…" rows={1} style={{flex:1,padding:'9px 12px',border:'1px solid #e2e8f0',borderRadius:10,fontSize:13,resize:'none',outline:'none',background:'white',fontFamily:'inherit',maxHeight:80,overflowY:'auto',lineHeight:1.5}} onInput={e=>{e.target.style.height='auto';e.target.style.height=Math.min(e.target.scrollHeight,80)+'px';}}/>
                  <button onClick={()=>sendAI()} disabled={!aiInput.trim()||aiLoading} style={{width:40,height:40,borderRadius:10,border:'none',background:aiInput.trim()&&!aiLoading?rc:'#e2e8f0',color:aiInput.trim()&&!aiLoading?'white':'#94a3b8',cursor:aiInput.trim()&&!aiLoading?'pointer':'default',fontSize:18,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{aiLoading?'⏳':'➤'}</button>
                </div>
                <div style={{fontSize:10,color:'#94a3b8',marginTop:4,textAlign:'center'}}>Enter to send · Shift+Enter for new line</div>
              </div>
            </div>
          )}

          {/* ── Team Chat Tab ────────────────────────────────────────────── */}
          {role&&!roleScreen&&tab==='team'&&(
            <div style={{flex:1,display:'flex',overflow:'hidden'}}>

              {/* Sidebar */}
              <div style={{width:196,borderRight:'1px solid #f1f5f9',display:'flex',flexDirection:'column',flexShrink:0,background:'#fafafa'}}>
                {/* Room list */}
                <div style={{padding:'10px 10px 5px',fontSize:10,fontWeight:800,color:'#94a3b8',textTransform:'uppercase',letterSpacing:'0.05em'}}>Rooms</div>
                <div className="n-sc" style={{flex:1,overflowY:'auto'}}>
                  {rooms.filter(r=>r.type!=='dm').map(r=>(
                    <button key={r.id} className={`n-rm${room===r.id?' act':''}`} onClick={()=>setRoom(r.id)} style={{width:'100%',display:'flex',alignItems:'center',gap:7,padding:'8px 10px',border:'none',borderRight:'3px solid transparent',background:'transparent',cursor:'pointer',textAlign:'left',fontSize:13,color:'#475569',fontWeight:500,transition:'background 0.1s'}}>
                      <span>{r.icon}</span>
                      <span style={{flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.name}</span>
                      {(unreadMap[r.id]||0)>0&&<span style={{background:'#dc2626',color:'white',borderRadius:20,padding:'1px 5px',fontSize:10,fontWeight:800,flexShrink:0}}>{unreadMap[r.id]}</span>}
                    </button>
                  ))}
                  {/* DMs */}
                  {rooms.filter(r=>r.type==='dm').length>0&&(
                    <>
                      <div style={{padding:'8px 10px 4px',fontSize:10,fontWeight:800,color:'#94a3b8',textTransform:'uppercase',letterSpacing:'0.05em',marginTop:4}}>Direct</div>
                      {rooms.filter(r=>r.type==='dm').map(r=>{
                        const ou=presence.find(u=>u.user_id===r.other_id);
                        return (
                          <button key={r.id} className={`n-rm${room===r.id?' act':''}`} onClick={()=>setRoom(r.id)} style={{width:'100%',display:'flex',alignItems:'center',gap:7,padding:'7px 10px',border:'none',borderRight:'3px solid transparent',background:'transparent',cursor:'pointer',textAlign:'left'}}>
                            <div style={{position:'relative',flexShrink:0}}>
                              <div style={{width:26,height:26,borderRadius:'50%',background:RC[ou?.role||'']||'#94a3b8',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,color:'white',fontWeight:700}}>{(r.name||'?')[0].toUpperCase()}</div>
                              <div style={{position:'absolute',bottom:-1,right:-1}}><StatusDot status={ou?.status||'offline'} size={8}/></div>
                            </div>
                            <span style={{fontSize:12,color:'#475569',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1}}>{r.name}</span>
                            {(unreadMap[r.id]||0)>0&&<span style={{background:'#dc2626',color:'white',borderRadius:20,padding:'1px 5px',fontSize:10,fontWeight:800}}>{unreadMap[r.id]}</span>}
                          </button>
                        );
                      })}
                    </>
                  )}
                  {/* People */}
                  <div style={{padding:'8px 10px 4px',fontSize:10,fontWeight:800,color:'#94a3b8',textTransform:'uppercase',letterSpacing:'0.05em',marginTop:4}}>People</div>
                  {presence.filter(u=>u.user_id!==userId).map(u=>(
                    <div key={u.user_id} className="n-usr" style={{display:'flex',alignItems:'center',gap:6,padding:'6px 10px',borderRadius:0}} onClick={()=>openDM(u.user_id,u.user_name)}>
                      <div style={{position:'relative',flexShrink:0}}>
                        <div style={{width:26,height:26,borderRadius:'50%',background:RC[u.role]||'#64748b',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,color:'white',fontWeight:700}}>{(u.user_name||'?')[0].toUpperCase()}</div>
                        <div style={{position:'absolute',bottom:-1,right:-1}}><StatusDot status={u.status} size={8}/></div>
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12,fontWeight:600,color:'#334155',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{u.user_name}</div>
                        <div style={{fontSize:10,color:'#94a3b8'}}>{RE[u.role]||''} {u.role}</div>
                      </div>
                      {u.status==='online'&&(
                        <button onClick={e=>{e.stopPropagation();startCall(u.user_id,u.user_name,'audio');}} title="Audio call" style={{background:'none',border:'none',cursor:'pointer',fontSize:13,padding:'2px',opacity:0.7,flexShrink:0}}>📞</button>
                      )}
                    </div>
                  ))}
                  {presence.filter(u=>u.user_id!==userId).length===0&&(
                    <div style={{padding:'14px 10px',fontSize:12,color:'#94a3b8',textAlign:'center'}}>No one else online yet</div>
                  )}
                </div>
              </div>

              {/* Message area */}
              <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
                {/* Room header */}
                <div style={{padding:'10px 14px',borderBottom:'1px solid #f1f5f9',display:'flex',alignItems:'center',gap:8,flexShrink:0,background:'white'}}>
                  {(()=>{
                    const rm=rooms.find(r=>r.id===room);
                    const isDM=rm?.type==='dm';
                    const ou=isDM?presence.find(u=>u.user_id===rm?.other_id):null;
                    return <>
                      <span style={{fontSize:18}}>{rm?.icon||'💬'}</span>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:700,fontSize:13,color:'#1e293b'}}>{rm?.name||room}</div>
                        {ou&&<div style={{fontSize:11,color:ou.status==='online'?'#16a34a':'#94a3b8'}}>{ou.status==='online'?'● Online':'○ Offline'} · {RE[ou.role]||''} {ou.role}</div>}
                      </div>
                      {isDM&&ou?.status==='online'&&(
                        <div style={{display:'flex',gap:6}}>
                          <button onClick={()=>startCall(rm.other_id,rm.name,'audio')} style={{padding:'6px 11px',background:'#f0fdf4',color:'#16a34a',border:'1px solid #bbf7d0',borderRadius:7,cursor:'pointer',fontSize:12,fontWeight:700}}>📞 Call</button>
                          <button onClick={()=>startCall(rm.other_id,rm.name,'video')} style={{padding:'6px 11px',background:'#eff6ff',color:'#2563eb',border:'1px solid #bfdbfe',borderRadius:7,cursor:'pointer',fontSize:12,fontWeight:700}}>📹 Video</button>
                        </div>
                      )}
                    </>;
                  })()}
                </div>

                {/* Messages */}
                <div className="n-sc" style={{flex:1,overflowY:'auto',padding:'12px 14px 6px',display:'flex',flexDirection:'column',gap:1}}>
                  {(msgMap[room]||[]).map((msg,i)=>{
                    const isMe=msg.sender_id===userId;
                    const isSys=msg.msg_type==='system';
                    const isCall=msg.msg_type==='call';
                    if(isSys) return <div key={msg.id||i} style={{textAlign:'center',fontSize:11,color:'#94a3b8',padding:'5px 0'}}>{msg.message}</div>;
                    if(isCall) return <div key={msg.id||i} style={{textAlign:'center',fontSize:12,color:'#2563eb',padding:'5px 0',background:'#eff6ff',borderRadius:8,margin:'3px 0'}}>{msg.message}</div>;
                    const prev=(msgMap[room]||[])[i-1];
                    const showSender=!prev||prev.sender_id!==msg.sender_id||prev.msg_type==='system';
                    const sc=RC[msg.sender_role]||'#64748b';
                    return (
                      <div key={msg.id||i} style={{display:'flex',flexDirection:isMe?'row-reverse':'row',gap:7,alignItems:'flex-end',marginTop:showSender&&i>0?8:1}}>
                        {!isMe&&showSender&&<div style={{width:28,height:28,borderRadius:'50%',background:sc,display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,color:'white',fontWeight:700,flexShrink:0}}>{(msg.sender_name||'?')[0].toUpperCase()}</div>}
                        {!isMe&&!showSender&&<div style={{width:28,flexShrink:0}}/>}
                        <div style={{maxWidth:'72%'}}>
                          {showSender&&!isMe&&<div style={{fontSize:11,fontWeight:700,color:sc,marginBottom:2,paddingLeft:4}}>{msg.sender_name} <span style={{color:'#94a3b8',fontWeight:400}}>{RE[msg.sender_role]||''}</span></div>}
                          <div style={{padding:'8px 12px',borderRadius:isMe?'14px 14px 3px 14px':'3px 14px 14px 14px',background:isMe?sc:'white',color:isMe?'white':'#334155',boxShadow:isMe?'none':'0 1px 3px rgba(0,0,0,0.06)',border:isMe?'none':'1px solid #f1f5f9',fontSize:13,lineHeight:1.45,wordBreak:'break-word',whiteSpace:'pre-wrap'}}>
                            {msg.message}
                          </div>
                          <div style={{fontSize:10,color:'#94a3b8',marginTop:2,textAlign:isMe?'right':'left',padding:'0 4px'}}>{fmtTime(msg.created_at)}</div>
                        </div>
                      </div>
                    );
                  })}
                  {(msgMap[room]||[]).length===0&&(
                    <div style={{textAlign:'center',color:'#94a3b8',padding:'44px 20px',fontSize:13}}>
                      No messages yet in <strong>{rooms.find(r=>r.id===room)?.name||room}</strong>.<br/>Be the first to say something!
                    </div>
                  )}
                  <div ref={chatEnd}/>
                </div>

                {/* Input */}
                <div style={{padding:'8px 12px 12px',borderTop:'1px solid #f1f5f9',background:'#fafafa',flexShrink:0}}>
                  <div style={{display:'flex',gap:7,alignItems:'flex-end'}}>
                    <textarea ref={chatRef} value={chatIn} onChange={e=>setChatIn(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();sendMsg();}}} placeholder={`Message ${rooms.find(r=>r.id===room)?.name||'#'+room}…`} rows={1} style={{flex:1,padding:'9px 12px',border:'1px solid #e2e8f0',borderRadius:10,fontSize:13,resize:'none',outline:'none',background:'white',fontFamily:'inherit',maxHeight:80,overflowY:'auto',lineHeight:1.5}} onInput={e=>{e.target.style.height='auto';e.target.style.height=Math.min(e.target.scrollHeight,80)+'px';}}/>
                    <button onClick={sendMsg} disabled={!chatIn.trim()||sending} style={{width:40,height:40,borderRadius:10,border:'none',background:chatIn.trim()&&!sending?'#2563eb':'#e2e8f0',color:chatIn.trim()&&!sending?'white':'#94a3b8',cursor:chatIn.trim()&&!sending?'pointer':'default',fontSize:18,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,transition:'all 0.15s'}}>{sending?'⏳':'➤'}</button>
                  </div>
                  <div style={{fontSize:10,color:'#94a3b8',marginTop:4,textAlign:'center'}}>Enter to send · Click 📞 on any online user to call them</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
